import { type Dispatch, type SetStateAction, useCallback, useRef, useState } from "react";

import {
  type MessageSearchResult,
  type MessageSummary,
  searchMessages,
  type SearchMessagesOptions,
} from "./messages-api";

export type SearchTimePreset = "1h" | "24h" | "7d" | "custom" | "none";

export interface SearchFilters {
  fromSender: string;
  subjectContains: string;
  timePreset: SearchTimePreset;
  /** Only used when timePreset === "custom". ISO datetime. */
  customFrom: string;
  /** Only used when timePreset === "custom". ISO datetime. */
  customTo: string;
}

export const DEFAULT_FILTERS: SearchFilters = {
  fromSender: "",
  subjectContains: "",
  timePreset: "none",
  customFrom: "",
  customTo: "",
};

export interface UseSearchMessagesResult {
  filters: SearchFilters;
  setFilters: Dispatch<SetStateAction<SearchFilters>>;
  items: MessageSummary[];
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  /** True when a search has been submitted at least once. */
  hasSearched: boolean;
  /** True when the form has at least one active filter. */
  hasActiveFilter: boolean;
  search: () => Promise<void>;
  loadNextPage: () => void;
  clearFilters: () => void;
}

const PAGE_SIZE = 50;

function filtersToOptions(filters: SearchFilters): SearchMessagesOptions | null {
  const opts: SearchMessagesOptions = { limit: PAGE_SIZE };
  if (filters.fromSender.trim()) opts.fromSender = filters.fromSender.trim();
  if (filters.subjectContains.trim()) opts.subjectContains = filters.subjectContains.trim();

  if (filters.timePreset === "1h") {
    opts.from = new Date(Date.now() - 60 * 60 * 1000).toISOString();
  } else if (filters.timePreset === "24h") {
    opts.from = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  } else if (filters.timePreset === "7d") {
    opts.from = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  } else if (filters.timePreset === "custom") {
    // customFrom/customTo are raw `datetime-local` strings (interpreted as
    // local time by the input). Convert to ISO at submit time so the API
    // receives a timezone-anchored instant instead of a naked local clock.
    if (filters.customFrom) opts.from = new Date(filters.customFrom).toISOString();
    if (filters.customTo) opts.to = new Date(filters.customTo).toISOString();
  }

  const hasFilter =
    opts.fromSender !== undefined ||
    opts.subjectContains !== undefined ||
    opts.from !== undefined ||
    opts.to !== undefined;

  return hasFilter ? opts : null;
}

export function hasActiveFilter(filters: SearchFilters): boolean {
  return filtersToOptions(filters) !== null;
}

export function useSearchMessages(baseUrl: string): UseSearchMessagesResult {
  const [filters, setFilters] = useState<SearchFilters>(DEFAULT_FILTERS);
  const [items, setItems] = useState<MessageSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  /**
   * Monotonic request id — stale responses from a previous search are
   * discarded when a new search fires before the old one resolves.
   * Same pattern as use-namespaces-list.ts and use-messages-list.ts.
   */
  const requestIdRef = useRef(0);

  const load = useCallback(
    async (
      activeFilters: SearchFilters,
      cursor: string | undefined,
      append: boolean
    ): Promise<void> => {
      const opts = filtersToOptions(activeFilters);
      if (opts === null) return;

      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      setHasSearched(true);

      if (!append) {
        setItems([]);
        setNextCursor(null);
        setError(null);
      }

      if (cursor) opts.cursor = cursor;

      try {
        const result: MessageSearchResult = await searchMessages(baseUrl, opts);
        if (requestId !== requestIdRef.current) return;
        setItems((prev) => (append ? [...prev, ...result.items] : result.items));
        setNextCursor(result.nextCursor);
        setError(null);
      } catch (e) {
        if (requestId !== requestIdRef.current) return;
        setError(e instanceof Error ? e.message : "Search failed");
      } finally {
        if (requestId === requestIdRef.current) setIsLoading(false);
      }
    },
    [baseUrl]
  );

  const search = useCallback((): Promise<void> => {
    return load(filters, undefined, false);
  }, [filters, load]);

  const loadNextPage = useCallback((): void => {
    if (!nextCursor) return;
    void load(filters, nextCursor, true);
  }, [nextCursor, filters, load]);

  const clearFilters = useCallback((): void => {
    setFilters(DEFAULT_FILTERS);
    setItems([]);
    setNextCursor(null);
    setError(null);
    setHasSearched(false);
    requestIdRef.current += 1;
  }, []);

  return {
    filters,
    setFilters,
    items,
    isLoading,
    error,
    hasMore: nextCursor !== null,
    hasSearched,
    hasActiveFilter: hasActiveFilter(filters),
    search,
    loadNextPage,
    clearFilters,
  };
}
